/**
 * Remembers which notification-centre tab the user last looked at, so reopening the bell
 * lands where they left off instead of resetting to "All" every time.
 *
 * <p>Kept in localStorage rather than a cookie: it is a per-device UI preference the server
 * has no interest in, so there is no reason to attach it to every request. Same mechanism the
 * People page uses for its view mode.
 *
 * <p>Every access is guarded — Safari private mode throws on localStorage rather than
 * returning null, and a preference is never worth breaking the shell over.
 */

import { useCallback, useState } from 'react';

export type NotificationTab = 'all' | 'unread' | 'needs_you';

const TABS: NotificationTab[] = ['all', 'unread', 'needs_you'];

export const NOTIFICATION_TAB_KEY = 'kudos.notifications.tab';

const DEFAULT_TAB: NotificationTab = 'all';

/** The stored tab, or "all" when nothing valid is stored. */
export function readNotificationTab(): NotificationTab {
  try {
    const stored = localStorage.getItem(NOTIFICATION_TAB_KEY) as NotificationTab | null;
    return stored && TABS.includes(stored) ? stored : DEFAULT_TAB;
  } catch {
    return DEFAULT_TAB;
  }
}

/** Persists the chosen tab. Failures are ignored: the choice still applies for this session. */
export function writeNotificationTab(tab: NotificationTab): void {
  try {
    localStorage.setItem(NOTIFICATION_TAB_KEY, tab);
  } catch {
    /* storage unavailable — in-memory only */
  }
}

/**
 * The notification-centre tab as state that persists itself.
 *
 * <p>Deliberately returns only a persisting setter and no raw state setter. An earlier version
 * exposed `useState` directly and something else in the shell called the plain setter to reset
 * the tab on close — which left storage saying "unread" while the component said "all", so the
 * choice appeared to survive a refresh but not a close. With no raw setter in scope, that class
 * of mismatch cannot be written.
 */
export function useNotificationTab(): [NotificationTab, (tab: NotificationTab) => void] {
  const [tab, setTab] = useState<NotificationTab>(readNotificationTab);

  const select = useCallback((next: NotificationTab) => {
    setTab(next);
    writeNotificationTab(next);
  }, []);

  return [tab, select];
}
